import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    Calendar as CalendarIcon,
    Check,
    ChevronsUpDown,
    Plus,
    RefreshCw,
    Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AdminLayoutWithDetails } from '@apps/web/components/admin/admin_layout_with_details';
import { AdminModeBadge } from '@apps/web/components/admin/admin_mode_badge';
import { Button } from '@apps/web/components/ui/button';
import { Calendar } from '@apps/web/components/ui/calendar';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatDateAndTimeWithTz } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminOrganizationCarePlanSettings,
    useAdminOrganizations,
} from '@apps/web/swr_data_fetching/admin_organization_queries';
import {
    type SampleShiftRead,
    type SampleShiftsResponse,
    type SummarizeCarePlanResponse,
} from '@libraries/typescript/api_client/schema_types';

type ShiftTimeFilter = 'past' | 'future' | 'all';

export const Route = createFileRoute('/_app/admin/care-plan-previewer')({
    component: CarePlanPreviewerPage,
    head: () => ({
        meta: [{ title: 'Care Plan Previewer | Phoebe Admin' }],
    }),
});

function CarePlanPreviewerPage() {
    const adminMode = useAdminMode();
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<
        string | null
    >(null);
    // Ref to track current org ID for race condition prevention
    const currentOrgIdRef = useRef<string | null>(null);
    const currentAdminModeRef = useRef(adminMode);
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const [orgSearch, setOrgSearch] = useState('');

    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [shiftTimeFilter, setShiftTimeFilter] =
        useState<ShiftTimeFilter>('all');
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

    const [sampledShifts, setSampledShifts] = useState<Array<SampleShiftRead>>(
        [],
    );
    const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(
        new Set(),
    );
    const [isSampling, setIsSampling] = useState(false);

    const [draftEssentialItems, setDraftEssentialItems] = useState<
        Array<string>
    >([]);
    const [draftCustomInstructions, setDraftCustomInstructions] = useState('');

    const [previewResults, setPreviewResults] = useState<
        Record<string, SummarizeCarePlanResponse>
    >({});
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

    const [isSaving, setIsSaving] = useState(false);

    const { data: organizationsResponse, isLoading: isLoadingOrgs } =
        useAdminOrganizations({
            ...(orgSearch && { search: orgSearch }),
            per_page: 50,
        });

    const { data: carePlanSettings, mutate: mutateCarePlanSettings } =
        useAdminOrganizationCarePlanSettings(selectedOrganizationId);

    const organizations = useMemo(
        () => organizationsResponse?.organizations ?? [],
        [organizationsResponse],
    );
    const sortedOrganizations = useMemo(() => {
        const ordered = [...organizations];
        ordered.sort((a, b) => {
            const labelA = a.internal_name?.trim() || a.name;
            const labelB = b.internal_name?.trim() || b.name;
            return labelA.localeCompare(labelB);
        });
        return ordered;
    }, [organizations]);

    const selectedOrganization = useMemo(
        () => organizations.find((o) => o.id === selectedOrganizationId),
        [organizations, selectedOrganizationId],
    );

    const selectedOrganizationLabel = selectedOrganization
        ? selectedOrganization.internal_name?.trim() ||
          selectedOrganization.name
        : null;
    const selectedOrganizationTimezone =
        selectedOrganization?.timezone ?? 'UTC';

    useEffect(() => {
        currentAdminModeRef.current = adminMode;
        setSampledShifts([]);
        setSelectedShiftIds(new Set());
        setPreviewResults({});
        setIsSampling(false);
        setIsGeneratingPreview(false);
        setIsSaving(false);
    }, [adminMode]);

    const handleOrganizationSelect = (orgId: string | null) => {
        currentOrgIdRef.current = orgId;
        setSelectedOrganizationId(orgId);
        // Clear all state when org changes
        setSampledShifts([]);
        setSelectedShiftIds(new Set());
        setPreviewResults({});
        setDraftEssentialItems([]);
        setDraftCustomInstructions('');
        setStartDate(undefined);
        setEndDate(undefined);
        setShiftTimeFilter('all');
    };

    const loadSettingsIntoDraft = () => {
        if (carePlanSettings) {
            setDraftEssentialItems(carePlanSettings.essential_items ?? []);
            setDraftCustomInstructions(
                carePlanSettings.custom_instructions ?? '',
            );
        }
    };

    const handleSampleShifts = async () => {
        if (!selectedOrganizationId) {
            return;
        }

        const orgIdAtStart = selectedOrganizationId;
        const adminModeAtStart = adminMode;
        setIsSampling(true);
        try {
            const api = getApiClient();
            const queryParams = {
                limit: 5,
                mode: adminMode,
                ...(startDate ? { start_date: startDate.toISOString() } : {}),
                ...(endDate ? { end_date: endDate.toISOString() } : {}),
                shift_time_filter: shiftTimeFilter,
            };

            const response: SampleShiftsResponse = await api.get({
                url: '/admin/organizations/{organization_id}/shifts/sample-with-care-plans',
                pathParams: { organization_id: selectedOrganizationId },
                queryParams,
                mode: adminMode,
            });
            // Check if org changed during the request
            if (
                currentOrgIdRef.current !== orgIdAtStart ||
                currentAdminModeRef.current !== adminModeAtStart
            ) {
                return;
            }
            setSampledShifts(response.shifts);
            // Select all shifts by default
            setSelectedShiftIds(new Set(response.shifts.map((s) => s.id)));
            setPreviewResults({});
        } catch (error) {
            console.error('Failed to sample shifts:', error);
            toast.error('Failed to sample shifts', {
                persistNotification: false,
            });
        } finally {
            if (
                currentOrgIdRef.current === orgIdAtStart &&
                currentAdminModeRef.current === adminModeAtStart
            ) {
                setIsSampling(false);
            }
        }
    };

    const handleGeneratePreview = async () => {
        if (!selectedOrganizationId || selectedShiftIds.size === 0) {
            return;
        }

        const orgIdAtStart = selectedOrganizationId;
        const adminModeAtStart = adminMode;
        setIsGeneratingPreview(true);
        setPreviewResults({});
        try {
            const api = getApiClient();
            const shiftIds = Array.from(selectedShiftIds);
            const previews = await Promise.all(
                shiftIds.map(async (shiftId) => {
                    const response: SummarizeCarePlanResponse = await api.post({
                        url: '/organizations/{organization_id}/shifts/summarize-care-plan',
                        pathParams: {
                            organization_id: selectedOrganizationId,
                        },
                        // The previewer follows the admin mode toggle, not
                        // the global product sandbox toggle this product
                        // route would otherwise inherit via X-Mode.
                        mode: adminMode,
                        body: {
                            shift_ids: [shiftId],
                            draft_essential_items: draftEssentialItems,
                            draft_custom_instructions:
                                draftCustomInstructions.trim() || null,
                        },
                    });
                    return { shiftId, response };
                }),
            );
            // Check if org changed during the request
            if (
                currentOrgIdRef.current !== orgIdAtStart ||
                currentAdminModeRef.current !== adminModeAtStart
            ) {
                return;
            }
            const nextResults: Record<string, SummarizeCarePlanResponse> = {};
            for (const { shiftId, response } of previews) {
                nextResults[shiftId] = response;
            }
            setPreviewResults(nextResults);
        } catch (error) {
            console.error('Failed to generate preview:', error);
            toast.error('Failed to generate preview', {
                persistNotification: false,
            });
        } finally {
            if (
                currentOrgIdRef.current === orgIdAtStart &&
                currentAdminModeRef.current === adminModeAtStart
            ) {
                setIsGeneratingPreview(false);
            }
        }
    };

    const handleSaveSettings = async () => {
        if (!selectedOrganizationId) {
            return;
        }

        const confirmed = window.confirm(
            'Are you sure you want to save these settings? This will update the care plan settings for this organization.',
        );
        if (!confirmed) {
            return;
        }

        const orgIdAtStart = selectedOrganizationId;
        const adminModeAtStart = adminMode;
        setIsSaving(true);
        try {
            const api = getApiClient();
            await api.put({
                url: '/admin/organizations/{organization_id}/care-plan-settings',
                pathParams: { organization_id: selectedOrganizationId },
                body: {
                    essential_items: draftEssentialItems,
                    custom_instructions: draftCustomInstructions || null,
                },
            });
            // Check if org changed during the request
            if (
                currentOrgIdRef.current !== orgIdAtStart ||
                currentAdminModeRef.current !== adminModeAtStart
            ) {
                return;
            }
            await mutateCarePlanSettings();
            toast.success('Care plan settings saved', {
                persistNotification: false,
            });
        } catch (error) {
            console.error('Failed to save settings:', error);
            toast.error('Failed to save settings', {
                persistNotification: false,
            });
        } finally {
            if (
                currentOrgIdRef.current === orgIdAtStart &&
                currentAdminModeRef.current === adminModeAtStart
            ) {
                setIsSaving(false);
            }
        }
    };

    const handleAddEssentialItem = () => {
        setDraftEssentialItems([...draftEssentialItems, '']);
    };

    const handleRemoveEssentialItem = (index: number) => {
        setDraftEssentialItems(
            draftEssentialItems.filter((_, i) => i !== index),
        );
    };

    const handleUpdateEssentialItem = (index: number, value: string) => {
        const updated = [...draftEssentialItems];
        updated[index] = value;
        setDraftEssentialItems(updated);
    };

    const handleToggleShift = (shiftId: string) => {
        const newSelected = new Set(selectedShiftIds);
        if (newSelected.has(shiftId)) {
            newSelected.delete(shiftId);
        } else {
            newSelected.add(shiftId);
        }
        setSelectedShiftIds(newSelected);
    };

    const handleSelectAllShifts = () => {
        setSelectedShiftIds(new Set(sampledShifts.map((s) => s.id)));
    };

    const handleDeselectAllShifts = () => {
        setSelectedShiftIds(new Set());
    };

    return (
        <AdminLayoutWithDetails
            defaultListSize={50}
            minListSize={35}
            defaultDetailsSize={50}
            minDetailsSize={35}
            listContent={
                <ScrollArea className="h-full">
                    <div className="space-y-6 p-6">
                        <div className="space-y-2">
                            <Label>Organization</Label>
                            <Popover
                                open={orgPickerOpen}
                                onOpenChange={setOrgPickerOpen}
                            >
                                <PopoverTrigger asChild={true}>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={orgPickerOpen}
                                        className="w-full justify-between"
                                        disabled={isLoadingOrgs}
                                    >
                                        {selectedOrganizationLabel ??
                                            'Select organization...'}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[400px] p-0">
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search organizations..."
                                            value={orgSearch}
                                            onValueChange={setOrgSearch}
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                {isLoadingOrgs
                                                    ? 'Loading...'
                                                    : 'No organizations found.'}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {sortedOrganizations.map(
                                                    (org) => {
                                                        const label =
                                                            org.internal_name?.trim() ||
                                                            org.name;
                                                        const showSecondary =
                                                            org.internal_name?.trim() &&
                                                            org.internal_name !==
                                                                org.name;
                                                        return (
                                                            <CommandItem
                                                                key={org.id}
                                                                value={org.id}
                                                                onSelect={() => {
                                                                    handleOrganizationSelect(
                                                                        org.id,
                                                                    );
                                                                    setOrgPickerOpen(
                                                                        false,
                                                                    );
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        'mr-2 h-4 w-4',
                                                                        selectedOrganizationId ===
                                                                            org.id
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0',
                                                                    )}
                                                                />
                                                                <div className="flex min-w-0 flex-col">
                                                                    <span className="truncate">
                                                                        {label}
                                                                    </span>
                                                                    {showSecondary && (
                                                                        <span className="text-muted-foreground truncate text-xs">
                                                                            {
                                                                                org.name
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </CommandItem>
                                                        );
                                                    },
                                                )}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                        {selectedOrganizationId && (
                            <React.Fragment>
                                <Button
                                    variant="outline"
                                    onClick={loadSettingsIntoDraft}
                                    className="w-full"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Load Saved Settings
                                </Button>
                                <Card>
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <CardTitle className="text-base">
                                                Sample Shifts
                                            </CardTitle>
                                            <AdminModeBadge mode={adminMode} />
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div className="space-y-2">
                                            <Label>Shift Type</Label>
                                            <Select
                                                value={shiftTimeFilter}
                                                onValueChange={(
                                                    value: ShiftTimeFilter,
                                                ) => setShiftTimeFilter(value)}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="Select shift type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">
                                                        Past and Future Shifts
                                                    </SelectItem>
                                                    <SelectItem value="past">
                                                        Past Shifts
                                                    </SelectItem>
                                                    <SelectItem value="future">
                                                        Future Shifts
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-2">
                                                <Label>Start Date</Label>
                                                <Popover
                                                    open={startDatePickerOpen}
                                                    onOpenChange={
                                                        setStartDatePickerOpen
                                                    }
                                                >
                                                    <PopoverTrigger
                                                        asChild={true}
                                                    >
                                                        <Button
                                                            variant="outline"
                                                            className={cn(
                                                                'w-full justify-start text-left font-normal',
                                                                !startDate &&
                                                                    'text-muted-foreground',
                                                            )}
                                                        >
                                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                                            {startDate
                                                                ? format(
                                                                      startDate,
                                                                      'PPP',
                                                                  )
                                                                : 'Pick a date'}
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        className="w-auto p-0"
                                                        align="start"
                                                    >
                                                        <Calendar
                                                            mode="single"
                                                            selected={startDate}
                                                            onSelect={(
                                                                date,
                                                            ) => {
                                                                setStartDate(
                                                                    date,
                                                                );
                                                                setStartDatePickerOpen(
                                                                    false,
                                                                );
                                                            }}
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>End Date</Label>
                                                <Popover
                                                    open={endDatePickerOpen}
                                                    onOpenChange={
                                                        setEndDatePickerOpen
                                                    }
                                                >
                                                    <PopoverTrigger
                                                        asChild={true}
                                                    >
                                                        <Button
                                                            variant="outline"
                                                            className={cn(
                                                                'w-full justify-start text-left font-normal',
                                                                !endDate &&
                                                                    'text-muted-foreground',
                                                            )}
                                                        >
                                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                                            {endDate
                                                                ? format(
                                                                      endDate,
                                                                      'PPP',
                                                                  )
                                                                : 'Pick a date'}
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        className="w-auto p-0"
                                                        align="start"
                                                    >
                                                        <Calendar
                                                            mode="single"
                                                            selected={endDate}
                                                            onSelect={(
                                                                date,
                                                            ) => {
                                                                setEndDate(
                                                                    date,
                                                                );
                                                                setEndDatePickerOpen(
                                                                    false,
                                                                );
                                                            }}
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                        </div>
                                        {(startDate || endDate) && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setStartDate(undefined);
                                                    setEndDate(undefined);
                                                }}
                                                className="w-full"
                                            >
                                                Clear Date Range
                                            </Button>
                                        )}
                                        <Button
                                            onClick={handleSampleShifts}
                                            disabled={isSampling}
                                            className="w-full"
                                        >
                                            {isSampling ? (
                                                <React.Fragment>
                                                    <Spinner className="mr-2" />
                                                    Sampling…
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    <RefreshCw className="mr-2 h-4 w-4" />
                                                    Sample Shifts with Care
                                                    Plans
                                                </React.Fragment>
                                            )}
                                        </Button>
                                        {sampledShifts.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground text-sm">
                                                        {selectedShiftIds.size}{' '}
                                                        of{' '}
                                                        {sampledShifts.length}{' '}
                                                        selected
                                                    </span>
                                                    <div className="space-x-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={
                                                                handleSelectAllShifts
                                                            }
                                                        >
                                                            All
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={
                                                                handleDeselectAllShifts
                                                            }
                                                        >
                                                            None
                                                        </Button>
                                                    </div>
                                                </div>
                                                {sampledShifts.map((shift) => (
                                                    <div
                                                        key={shift.id}
                                                        className="flex items-start space-x-3 rounded-md border p-3"
                                                    >
                                                        <Checkbox
                                                            checked={selectedShiftIds.has(
                                                                shift.id,
                                                            )}
                                                            onCheckedChange={() =>
                                                                handleToggleShift(
                                                                    shift.id,
                                                                )
                                                            }
                                                        />
                                                        <div className="min-w-0 flex-1 space-y-1">
                                                            <div className="font-medium">
                                                                {
                                                                    shift.patient_name
                                                                }
                                                            </div>
                                                            <div className="text-muted-foreground text-xs">
                                                                {shift.assigned_caregiver_name ??
                                                                    'Unassigned'}
                                                            </div>
                                                            <div className="text-muted-foreground text-xs">
                                                                {formatDateAndTimeWithTz(
                                                                    shift.shift_start_time,
                                                                    selectedOrganizationTimezone,
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base">
                                            Draft Settings
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Essential Items</Label>
                                            <div className="space-y-2">
                                                {draftEssentialItems.map(
                                                    (item, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="flex items-center space-x-2"
                                                        >
                                                            <span className="text-muted-foreground w-6 text-sm">
                                                                {idx + 1}.
                                                            </span>
                                                            <Input
                                                                value={item}
                                                                onChange={(e) =>
                                                                    handleUpdateEssentialItem(
                                                                        idx,
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                placeholder="Enter essential item..."
                                                                className="flex-1"
                                                            />
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() =>
                                                                    handleRemoveEssentialItem(
                                                                        idx,
                                                                    )
                                                                }
                                                            >
                                                                <Trash2 className="h-4 w-4 text-red-500" />
                                                            </Button>
                                                        </div>
                                                    ),
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={
                                                        handleAddEssentialItem
                                                    }
                                                >
                                                    <Plus className="mr-2 h-4 w-4" />
                                                    Add Item
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Custom Instructions</Label>
                                            <Textarea
                                                value={draftCustomInstructions}
                                                onChange={(e) =>
                                                    setDraftCustomInstructions(
                                                        e.target.value,
                                                    )
                                                }
                                                rows={4}
                                                placeholder="Enter custom instructions for care plan summarization..."
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            </React.Fragment>
                        )}
                    </div>
                </ScrollArea>
            }
            detailsContent={
                <div className="space-y-6">
                    {!selectedOrganizationId && (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            Select an organization to begin
                        </div>
                    )}
                    {selectedOrganizationId && sampledShifts.length === 0 && (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            Sample shifts to see care plans
                        </div>
                    )}
                    {sampledShifts.length > 0 && selectedShiftIds.size > 0 && (
                        <React.Fragment>
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">
                                        Selected Care Plans
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="max-h-64 space-y-4 overflow-y-auto">
                                        {sampledShifts
                                            .filter((s) =>
                                                selectedShiftIds.has(s.id),
                                            )
                                            .map((shift) => (
                                                <div
                                                    key={shift.id}
                                                    className="space-y-1"
                                                >
                                                    <div className="text-sm font-medium">
                                                        {shift.patient_name}
                                                    </div>
                                                    <div className="bg-muted rounded p-3 text-sm whitespace-pre-wrap">
                                                        {
                                                            shift.patient_care_plan
                                                        }
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </CardContent>
                            </Card>
                            <Button
                                onClick={handleGeneratePreview}
                                disabled={
                                    isGeneratingPreview ||
                                    selectedShiftIds.size === 0
                                }
                                className="w-full"
                            >
                                {isGeneratingPreview ? (
                                    <React.Fragment>
                                        <Spinner className="mr-2" />
                                        Generating Previews…
                                    </React.Fragment>
                                ) : (
                                    'Generate Previews'
                                )}
                            </Button>
                            {Object.keys(previewResults).length > 0 && (
                                <React.Fragment>
                                    {sampledShifts
                                        .filter((shift) =>
                                            selectedShiftIds.has(shift.id),
                                        )
                                        .map((shift) => {
                                            const preview =
                                                previewResults[shift.id];
                                            if (!preview) {
                                                return null;
                                            }
                                            return (
                                                <Card key={shift.id}>
                                                    <CardHeader className="pb-3">
                                                        <CardTitle className="text-base">
                                                            {shift.patient_name}
                                                        </CardTitle>
                                                        <div className="text-muted-foreground text-xs">
                                                            {shift.assigned_caregiver_name ??
                                                                'Unassigned'}{' '}
                                                            ·{' '}
                                                            {formatDateAndTimeWithTz(
                                                                shift.shift_start_time,
                                                                selectedOrganizationTimezone,
                                                            )}
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        <div className="space-y-2">
                                                            <div className="text-sm font-medium">
                                                                Voice Summary
                                                            </div>
                                                            <div className="bg-muted rounded p-3 text-sm whitespace-pre-wrap">
                                                                {preview.summary ||
                                                                    '(empty)'}
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <div className="text-sm font-medium">
                                                                SMS Bullets
                                                            </div>
                                                            <div className="bg-muted rounded p-3 text-sm whitespace-pre-wrap">
                                                                {preview.care_plan_bullets ||
                                                                    '(empty)'}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    <Button
                                        onClick={handleSaveSettings}
                                        disabled={isSaving}
                                        className="w-full"
                                        variant="default"
                                    >
                                        {isSaving ? (
                                            <React.Fragment>
                                                <Spinner className="mr-2" />
                                                Saving…
                                            </React.Fragment>
                                        ) : (
                                            'Save Settings'
                                        )}
                                    </Button>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </div>
            }
        />
    );
}
